import React, {useState, useEffect, useMemo, useCallback, Fragment, memo} from 'react';
import {shallowEqual, useSelector, useDispatch} from 'react-redux';
import styled from 'styled-components';
import uuidv4 from 'uuid/v4';
import {isNullOrUndefined, labels} from '../../../../../../util/utils';
import Utility from '../../../../../../util/utilityfunctions';
import {
	entities,
	formBodyType,
	aresTaskViewRoutes,
	serviceURL,
	actionType,
	documentTypes
} from '../../../../../../util/uiconstants';
import {DocumentEngagementIdContext, DocumentTypeIdContext} from '../../../../Forms/CanvasFormContainer';
import {getSectionById, getSTEntityById, getTaskDocumentByDocumentId} from '../../../../../../selectors/Ares/selectors';
import LoadingIndicator from '../../../../../Common/LoadingIndicator/LoadingIndicator';
import AresFormBodyContainer from '../AresBodies/AresFormBodyContainer';
import ManageRisks from '../../../../../Common/RiskFactors/ManageRisks/ManageRisks';
import {useClearAllComments, useQuery} from '../../../../../../util/customHooks';
import TaskViewBreadCrumbs from '../BreadCrumbs/TaskViewBreadCrumbs';
import SectionCommentControl from '../SectionCommentControl';
import {RiskFactorsValidExpands, getRiskFactors} from '../../../../../../actions/RiskFactor/riskfactoractions';
import {getRisks} from '../../../../../../actions/Risk/riskactions';
import ActivityButtons from '../../../../Ares/ActivityButtons/ActivityButtons';
import ManageEstimates from '../../../../../Common/Estimates/ManageEstimates/ManageEstimates';
import CompareSnapshotLabel from '../../../../../Common/CanvasForm/CompareSnapshotLabel';
import TrackChangesHeaderDisplay from '../../../../../Common/TrackChanges/TrackChangesHeaderDisplay';
import env from '../../../../../../util/env';
import {getFormBodiesBySectionId} from '../../../../../../actions/FormData/formdataactions';
import CompareResponsesHeaderDisplay from '../../../../../Common/CompareResponses/CompareResponsesHeaderDisplay';
import {getOptionActionsBySectionId} from '../../../../../../selectors/Forms/formBodySelectors';
import Validation from '../../../../../Common/CanvasForm/Common/Validation';
import {getBodiesBySectionId} from '../../../../../../actions/Ares/compareFormsActions';
import {getAllDocuments} from '../../../../../../actions/Document/documentactions';
import Env from '../../../../../../util/env';
import {clientSideEventNames} from '../../../../../../util/uiconstants';
import EllipsesControl from '../../../../../Common/EllipsesControl/EllipsesControl';
import {useIsIndependenceOrProfile, useSelectedStEntityId} from '../../../../../../util/Ares/customHooks';
import {isEqual} from 'lodash';
import {useFrameData, useFrameKey} from '@ey/canvascoreservices';
import TaskViewFooter from './Footer/TaskViewFooter';
import Icon from '../../../../../Common/BaseComponentWrappers/MotifIcon';
import {MotifImageIcFilterNone24px, IconoirTools} from '@ey-xd/motif-icon';
import IndependenceDetails from './IndependenceDetails';
import Ellipses from '../../../../../Common/EllipsesControl/EllipsesPresets';
import FormRelatedEntities from './FormRelatedEntities';

function FormDetailsV2({
	taskId,
	documentId,
	headerId,
	sectionId,
	isTrackChangesView,
	isCompareResponsesView,
	isBodyDetailView = false,
	entityDetails
}) {
	const {engagementId, engagement, quickLink, document, section} = useSelector((storeState) => {
		return {
			engagementId: storeState.engagementId,
			engagement: storeState.engagement,
			quickLink: storeState.quickLink,
			document: getTaskDocumentByDocumentId(storeState, documentId),
			section: getSectionById(storeState, sectionId)
		};
	}, shallowEqual);

	const documentTypeId = document?.documentTypeId;

	const [selectedStEntityId] = useSelectedStEntityId();
	const selectedEntity = useSelector((store) => getSTEntityById(store, selectedStEntityId));
	const formBodyData = useSelector((store) => store.formBodyData);

	const risks = useSelector((store) => store.risks);

	const associateRiskActions = useSelector((store) =>
		getOptionActionsBySectionId(store, sectionId, (a) => a.actionTypeId === actionType.AssociateRisk)
	);

	const snapshotCompareDetails = useSelector((store) => store.snapshotCompareDetails);

	const [isLoading, setIsLoading] = useState(isBodyDetailView);
	const [showValidationLoader, setShowValidationLoader] = useState(false);
	const [showManageEstimatesButton, setShowManageEstimatesButton] = useState(false);
	const [showManageRiskButton, setShowManageRiskButton] = useState(false);
	const [showComment, setShowComment] = useState(0);
	const [haveValidationsCount, setHaveValidationsCount] = useState(false);
	const [bodyDataLoaded, setBodyDataLoaded] = useState(false);
	const [documentName, setDocumentName] = useState('');
	const [targetSection, setTargetSection] = useState({});
	const [associatedDocumentIds, setAssociatedDocumentIds] = useState([]);
	const [bodyTypeId, setBodyTypeId] = useState(undefined);

	const showBreadcrumbs = !useIsIndependenceOrProfile();
	const isIndependence = useIsIndependenceOrProfile();

	const dispatch = useDispatch();

	const query = useQuery();

	const frameData = useFrameData();

	const {frameKey} = useFrameKey();

	const activityName = useMemo(() => {
		return (
			section?.formSectionName ??
			section?.name +
				`${(section?.isDuplicate || section?.isCustom) && section?.customLabel ? ` - ${section?.customLabel}` : ''}`
		);
	}, [section]);

	const {knowledgeFormBodyId, formBodyId} = useMemo(() => {
		if (isBodyDetailView && formBodyData?.length > 0 && query.bodyid) {
			const formSection = formBodyData.find((data) => data.sectionId === Number(sectionId));

			if (Array.isArray(formSection?.bodies) && formSection?.bodies?.length > 0) {
				const body = formSection.bodies.find((body) => body.id === Number(query.bodyid));
				return {formBodyId: body?.id, knowledgeFormBodyId: body?.knowledgeId};
			}
		}

		return {};
	}, [formBodyData, isBodyDetailView, query.bodyid, sectionId]);

	const handleBodyDataLoaded = useCallback(() => {
		setBodyDataLoaded(true);
	}, []);

	const {setClearAllComments} = useClearAllComments(
		engagementId,
		document?.id,
		documentTypeId,
		entities.FormSection,
		sectionId,
		() => {
			setShowValidationLoader(false);
			setClearAllComments(false);
		}
	);

	const showComments = useCallback((count) => {
		setShowComment(count);
	}, []);

	const reloadSection = useCallback(() => {
		let url =
			env.getBaseURL(serviceURL, engagementId) +
			`forms/${documentId}/headers/${headerId}/sections/${sectionId}/Bodies?includeAction=true&includeRisks=true`;

		if (sectionId) {
			dispatch(getFormBodiesBySectionId(url));
		}
	}, [engagementId, documentId, headerId, sectionId, dispatch]);

	const clearComments = useCallback(() => {
		//Clear comments was removed from validations Memo dependencies because it was causing multiple rerenders on valodation component.
		setShowValidationLoader(true);
		setClearAllComments(true);
	}, []);

	const validations = useMemo(() => {
		return (
			<section className="SectionValidationWrapper">
				<Validation
					key={uuidv4()}
					documentId={documentId}
					headerId={headerId}
					sectionId={sectionId}
					bodyId={undefined}
					displayFor={'section'}
					clearComments={clearComments}
					setHaveValidationsCount={setHaveValidationsCount}
					bodyTypeId={bodyTypeId}
				/>
			</section>
		);
	}, [documentId, headerId, sectionId, clearComments, setHaveValidationsCount, bodyTypeId]);

	useEffect(() => {
		frameData.getData(frameKey).then((data) => {
			if (data) {
				setAssociatedDocumentIds(data.associatedDocumentIds);
			}
		});
	}, []);

	useEffect(() => {
		if (documentName !== document?.documentName) {
			if (document?.documentTypeId !== documentTypes.INDEPENDENCEFORMINDIVIDUAL) {
				setDocumentName(document?.documentName);
			} else if (document?.documentTypeId === documentTypes.INDEPENDENCEFORMINDIVIDUAL) {
				// Set Document name as User Name for Independence Form
				setDocumentName(document?.independenceInformation?.name);
			}
		}
	}, [document, documentName]);

	useEffect(() => {
		if (isBodyDetailView && document?.knowledgeFormId && documentTypeId) {
			const uri = `Documents?documentTypeIdList=${document.documentTypeId}&filterKnowledgeForm=${document.knowledgeFormId}&includeRelatedTasks=true&includeRelatedEntities=true`;

			dispatch(getAllDocuments(`${Env.getBaseURL(serviceURL, engagementId)}${uri}`, undefined, true));
		}
	}, [dispatch, documentTypeId, document?.knowledgeFormId, engagementId, isBodyDetailView]);

	useEffect(() => {
		if (isBodyDetailView && associatedDocumentIds.length > 0) {
			const data = {
				documentIDs: associatedDocumentIds
			};

			// This is prevent API from failing. If the documentId is not in the list, then don't call the API.
			// This is necessary API call for navigation in body details view.
			if (associatedDocumentIds.includes(documentId)) {
				dispatch(getBodiesBySectionId({engagementId, documentId, sectionId, data})).finally(() => {
					setIsLoading(false);
				});
			}
		}
	}, [documentId, engagementId, sectionId, dispatch, isBodyDetailView, associatedDocumentIds]);

	useEffect(() => {
		if (snapshotCompareDetails?.length > 0) {
			const sections = snapshotCompareDetails && snapshotCompareDetails?.find((c) => c.id === headerId)?.sections;
			const currentSection = sections && sections?.find((c) => c.id === sectionId);
			setTargetSection(currentSection);
		}
	}, [snapshotCompareDetails]);

	useEffect(() => {
		window.addEventListener(clientSideEventNames.aresBodyDataLoaded, handleBodyDataLoaded);

		return () => {
			window.removeEventListener(clientSideEventNames.aresBodyDataLoaded, handleBodyDataLoaded);
		};
	}, []);

	useEffect(() => {
		if (sectionId) {
			setShowManageRiskButton(false);
		}
	}, [sectionId]);

	useEffect(() => {
		if (!isNullOrUndefined(engagementId)) {
			// API calls not needed for Profile and Independence type forms
			if (Utility.isDocumentIndependenceOrProfileForm(documentTypeId) === false) {
				dispatch(
					getRiskFactors(engagementId, {
						expand: RiskFactorsValidExpands.formHeaders,
						filterMultiEntityIds: selectedStEntityId
							? selectedStEntityId
							: !isNullOrUndefined(document?.stEntityId)
							? document.stEntityId
							: undefined
					})
				);

				if (!risks?.data) {
					const queryParameters = {
						pageSize: -1,
						includeFormBodyOptions: true,
						orderBy: 'RiskName',
						orderByAscending: true
					};
					dispatch(getRisks(engagementId, queryParameters));
				}
			}
		}
	}, [dispatch, sectionId, engagementId, documentTypeId, selectedStEntityId]);

	useEffect(() => {
		let showManageAccountButton = false,
			showManageEstimateButton = false,
			showManageRiskButton = false;

		if (sectionId && formBodyData.length > 0) {
			const formBodyForSection = formBodyData.find((formBody) => {
				return Number(formBody.sectionId) === sectionId;
			});

			if (formBodyForSection && formBodyForSection.bodies && formBodyForSection.bodies.length > 0) {
				const bodies = formBodyForSection.bodies;

				const accountBodyFound = bodies.find((body) => {
					return (
						body.isDisplayed &&
						(body.bodyTypeId === formBodyType.RelateRiskAssertion ||
							body.bodyTypeId === formBodyType.AccountSummaryComponent ||
							body.bodyTypeId === formBodyType.DisplayLimitedRiskAccountsBody ||
							body.bodyTypeId === formBodyType.DefineEstimatesForAccounts ||
							body.bodyTypeId === formBodyType.AssessInheritRisk ||
							body.bodyTypeId === formBodyType.ALRAAccountList ||
							body.bodyTypeId === formBodyType.DisplayInsignificantAccountsBody)
					);
				});

				if (accountBodyFound) {
					showManageAccountButton = true;
				}

				const riskBodyFound = bodies.find((body) => {
					return body.isDisplayed && body.bodyTypeId === formBodyType.DisplayRiskAndRiskFactor;
				});

				if (riskBodyFound || associateRiskActions.length > 0) {
					showManageRiskButton = true;
				}

				const estimateBodyFound = bodies.find((body) => {
					return body.isDisplayed && body.bodyTypeId === formBodyType.DefineEstimatesForAccounts;
				});

				if (estimateBodyFound) {
					showManageEstimateButton = true;
				}

				const analyticsOrALRAAccountListBody = bodies.find(
					(body) =>
						body.bodyTypeId === formBodyType.AnalyticsOverview || body.bodyTypeId === formBodyType.ALRAAccountList
				);

				if (analyticsOrALRAAccountListBody) {
					setBodyTypeId(analyticsOrALRAAccountListBody?.bodyTypeId);
				}
			}
		}

		setShowManageRiskButton(showManageRiskButton);
		setShowManageEstimatesButton(showManageEstimateButton);
	}, [formBodyData, sectionId]);

	const showLoader = !sectionId || !document || isLoading;

	useEffect(() => {
		const windowDoc = window.document.getElementById('fit-navigation-panel');
		if (showLoader && windowDoc !== null) {
			windowDoc.classList.add('RightLoaderVisible');
		} else if (windowDoc !== null) {
			windowDoc.classList.remove('RightLoaderVisible');
		}
	}, [showLoader]);

	return (
		<Fragment>
			<StyledFormDetailsV2Wrapper
				className={`StyledFormDetailsWrapper customScrollbar mainResponsiveWrapper
							${isBodyDetailView && 'bodyDetailsViewContainer'}`}
			>
				{showLoader ? (
					<LoadingIndicator view={'fullview'} fullscreen styleName="LoadingIndicator StyledFormDetailsWrapperV2" />
				) : (
					<DocumentEngagementIdContext.Provider value={document?.engagementId}>
						<DocumentTypeIdContext.Provider value={documentTypeId}>
							{sectionId ? (
								<>
									<section
										className={`top-wrapper ${section?.isSummary ? 'summaryTopWrapper' : 'nonSummaryTopWrapper'} ${
											isIndependence ? 'independenceTopWrapper' : 'nonIndependenceTopWrapper'
										}`}
									>
										{!isBodyDetailView && showBreadcrumbs ? (
											<section className="breadcrumb-wrapper">
												<TaskViewBreadCrumbs
													taskId={taskId}
													documentId={documentId}
													headerId={headerId}
													sectionId={sectionId}
													isOnSummary={false}
												/>
											</section>
										) : (
											<section className="document-name">
												<EllipsesControl content={documentName} tooltip={documentName} noOfLines={1} />
											</section>
										)}
										{isIndependence && <IndependenceDetails document={document} />}
										{!isCompareResponsesView && !isBodyDetailView && (
											<section className="summary-task-right-side">
												<ActivityButtons
													baseDomain={`/${aresTaskViewRoutes.guidedworkflowV2}/`}
													documentId={documentId}
													engagementId={engagementId}
													currentTaskId={taskId}
													showShare={section?.hasSharing}
													showPrint={section?.hasPrint}
													showAssigment={section?.hasAssignments}
													showMarkComplete={section?.hasMarkComplete}
													showTrackChanges={section?.hasTrackChanges}
													showCompareResponse={section?.hasCompare}
													showAttributes={section?.hasAttributes}
													showApprovals={section?.hasApproval}
													showCopy={section?.hasSharing}
													sectionId={sectionId}
													isSummary={section?.isSummary}
													headerId={headerId}
													section={section}
												/>
											</section>
										)}
										{isTrackChangesView && !isCompareResponsesView && !isBodyDetailView && !section?.isSummary && (
											<section className="summary-task-right-side hide" />
										)}
									</section>
									{entityDetails && (
										<section
											className={`SectionTopComponent mainResponsiveWrapper ${isBodyDetailView && 'BodyDetailsView'}`}
										>
											{entityDetails}
										</section>
									)}
									<section
										className={`SectionTopNameWrapper mainResponsiveWrapper ${isCompareResponsesView &&
											'compareResponsesSectionTopNameWrapper'} ${section?.isSummary &&
											'nosection'} ${isBodyDetailView && 'BodyDetailsSectionTopNameWrapper BodyDetailsView'}`}
									>
										{isTrackChangesView ? (
											<section className="SnapshotDetailWrapper">
												{activityName && (
													<TrackChangesHeaderDisplay
														headerId={headerId}
														sectionId={sectionId}
														documentTypeId={documentTypeId}
														taskId={taskId}
														isSummary={section?.isSummary}
														activityName={activityName}
														isDuplicate={section?.isDuplicate}
													/>
												)}
											</section>
										) : isCompareResponsesView ? (
											<section className="SnapshotDetailWrapper">
												{activityName && <CompareResponsesHeaderDisplay activityName={activityName} />}
											</section>
										) : (
											<section className="sectionNameTrackChangesWrapper">
												<section className="SectionNameWrapper">
													{!section?.isSummary && (
														<section className={`SectionNameDetails ${isBodyDetailView && 'BodyDetailsSectionName'}`}>
															<Ellipses.Single>
																{`${section?.formSectionName || section?.name || ''}
																  ${(section?.isDuplicate || section?.isCustom) && section?.customLabel ? ` - ${section?.customLabel}` : ''}`}
															</Ellipses.Single>
															{section?.isDuplicate && (
																<Icon
																	className="duplicate-icon"
																	src={MotifImageIcFilterNone24px}
																	title={labels.duplicate}
																/>
															)}
														</section>
													)}
													{engagement.isMestEngagement &&
														engagement.isMultiEntityEngagement &&
														quickLink?.defaultMultiEntityId && (
															<FormRelatedEntities relatedEntities={document?.entities} />
														)}
													{document?.isShared && !section?.isDuplicate && (
														<section className="shared-icon" title={labels.sharedWithAnotherFITActivity}>
															{labels.shared}
														</section>
													)}
												</section>
											</section>
										)}
										{!isBodyDetailView && (
											<section className="SectionRighSideWrapper">
												{/*Track Changes - Compare Badge*/}
												{!isTrackChangesView && (
													<>
														{/*Manage Risk Wrapper*/}
														{showManageRiskButton && <ManageRisks onClose={() => reloadSection()} />}
														{showManageRiskButton && showManageEstimatesButton && (
															<span className="LinkSeparator">|</span>
														)}
														{/*Manage estimates Wrapper*/}
														{showManageEstimatesButton && <ManageEstimates onClose={() => reloadSection()} />}
													</>
												)}
												{section?.isCustom && (
													<section className="custom-icon">
														<Icon src={IconoirTools} title={labels.custom} />
													</section>
												)}
												{/*Comment & Validation Wrapper*/}
												<section
													className={`CommentsValidationWrapper
																	${showComment > 0 ? 'CommentIconVisible' : 'CommentIconNotVisible'}
																	${haveValidationsCount ? 'ValidationIconVisible' : 'ValidationIconNotVisible'}`}
												>
													{showValidationLoader && <LoadingIndicator fullscreen />}
													{/*Comments Wrapper*/}
													<SectionCommentControl
														documentId={documentId}
														headerId={headerId}
														sectionId={sectionId}
														showComments={showComments}
													/>
													{validations}
												</section>
											</section>
										)}
									</section>
									{!isCompareResponsesView && document?.embeddedBodyTypeIds !== undefined && (
										<AresFormBodyContainer
											key={`${document}-${headerId}-${sectionId}-${taskId}`}
											document={document}
											headerId={headerId}
											sectionId={sectionId}
											isBodyDetailView={isBodyDetailView}
											bodyDataLoaded={bodyDataLoaded}
										/>
									)}
									{bodyDataLoaded && !isBodyDetailView && (
										<TaskViewFooter
											document={document}
											engagementId={engagementId}
											headerId={headerId}
											sectionId={sectionId}
											taskId={taskId}
											setBodyDataLoaded={setBodyDataLoaded}
											className={`mainResponsiveWrapper ${
												isCompareResponsesView ? 'compareResponsesStyledAresViewFooter' : ''
											}`}
											setIsLoading={setIsLoading}
											isLoading={isLoading}
										/>
									)}
								</>
							) : (
								<section className="StyledAresFormBodyContainer mainResponsiveWrapper">
									<section className="emptyContainer">{labels.noRecordsAvailable}</section>
								</section>
							)}
						</DocumentTypeIdContext.Provider>
					</DocumentEngagementIdContext.Provider>
				)}
			</StyledFormDetailsV2Wrapper>
			{isBodyDetailView && (
				<TaskViewFooter
					document={document}
					engagementId={engagementId}
					headerId={headerId}
					sectionId={sectionId}
					taskId={taskId}
					knowledgeFormBodyId={knowledgeFormBodyId}
					formBodyId={formBodyId}
					setIsLoading={setIsLoading}
					isLoading={isLoading}
					className="mainResponsiveWrapper bodyDetailsFooter"
					isBodyDetailView={isBodyDetailView}
				/>
			)}
		</Fragment>
	);
}

export default memo(FormDetailsV2, isEqual);

const StyledFormDetailsV2Wrapper = styled.section`
	height: 100%;
	width: 100%;
	padding: 0;
	display: flex;
	flex-direction: column;

	&.bodyDetailsViewContainer {
		height: calc(100% - var(--px-84));
	}

	.mainResponsiveWrapper {
		padding: 0 var(--px-80) 0 var(--px-40) !important;
	}

	.top-wrapper {
		display: flex;
		justify-content: space-between;
		padding: var(--px-32) var(--px-40) 0 var(--px-40) !important;
		&.summaryTopWrapper {
			margin-bottom: var(--px-30);
		}
		&.independenceTopWrapper {
			flex-direction: column;
			.document-name {
				padding-top: 0 !important;
			}
		}
		.breadcrumb-wrapper {
			width: var(--px-665);
			display: flex;
			align-items: center;
			.motif-breadcrumb {
				&.mainResponsiveWrapper {
					@media only screen and (min-width: 1921px) {
						margin: 0;
						padding: 0 !important;
					}
				}
				.motif-breadcrumb-list {
					.motif-breadcrumb-list-item {
						width: auto;
						max-width: var(--px-160);
						.motif-breadcrumb-separator {
							width: var(--px-16);
							height: var(--px-16);
						}
						.motif-breadcrumb-text {
							min-width: var(--px-35);
							width: auto;
							max-width: calc(100% - var(--px-20));
							outline: none;
							box-shadow: none;
							&:focus {
								outline: none;
								box-shadow: none;
							}
							.BreadcrumbSummaryLabelLink {
								width: 100%;
							}
						}
						&:first-child {
							.motif-breadcrumb-text {
								max-width: 100%;
							}
						}
					}
				}
			}
		}
		.document-name {
			display: flex;
			align-items: center;
			font-size: var(--px-32);
			font-weight: 400;
			line-height: var(--px-42);
			padding: var(--px-36) 0 0 0;
			width: 100%;
		}
		.summary-task-right-side {
			min-width: var(--px-650);
			max-width: 100%;
			display: flex;
			align-items: center;
			justify-content: flex-end;
			margin: 0;
			.StyledFetchLoader {
				width: 100% !important;
			}
			.motif-progress-loader-container {
				z-index: 999999;
			}
			.viewChangesWrapper {
				display: inline-flex;
				align-items: center;
				.StyledClickableIconLabel {
					margin-right: var(--px-20);
					svg {
						width: var(--px-18) !important;
						height: var(--px-18) !important;
					}
				}
				.sharing-activity {
					display: flex;
					max-width: var(--px-300);
					align-items: center;
				}
				.StyledViewChangesButton {
					margin-right: var(--px-20);
				}
				.linkInProgress,
				.unlinkInProgress {
					display: flex;
					align-items: center;
					.motif-button {
						.motif-icon {
							svg {
								width: var(--px-18) !important;
								height: var(--px-18) !important;
								transform: rotateY(0deg) rotateX(-180deg) rotateZ(90deg);
								fill: var(--neutrals-700);
								path {
									fill: var(--neutrals-700);
								}
							}
						}
					}
					.linkInProgressStatus,
					.unlinkInProgressStatus {
						font-size: var(--px-14);
						font-weight: 700;
						line-height: var(--px-22);
						color: var(--neutrals-700);
						width: calc(100% - var(--px-26));
						margin-right: var(--px-10);
					}
				}
				.shared-pill {
					display: flex;
					align-items: center;
					border: var(--px-1) solid var(--neutrals-300);
					background-color: var(--neutrals-00white);
					border-radius: var(--px-12);
					padding: var(--px-4) var(--px-12);
					min-width: var(--px-90);
					width: var(--px-90);
					height: var(--px-30);
					margin-right: var(--px-16);
					.sharedPillText {
						font-size: var(--px-12);
						font-weight: 400;
						line-height: var(--px-16);
						color: var(--neutrals-900);
						width: calc(100% - var(--px-26));
						margin-right: var(--px-10);
					}
					.motif-dropdown {
						margin: 0;
						height: var(--px-16);
						.motif-dropdown-trigger {
							svg {
								height: var(--px-16) !important;
								width: var(--px-16) !important;
								fill: var(--neutrals-900);
								path {
									fill: var(--neutrals-900);
								}
							}
						}
						.motif-dropdown-menu {
							right: calc(var(--px-10) * -1);
							top: var(--px-25) !important;
						}
					}
					.motif-tooltip-wrapper {
						display: flex;
						.motif-icon {
							height: var(--px-16);
							svg {
								height: var(--px-16) !important;
								width: var(--px-16) !important;
								fill: var(--neutrals-900);
								path {
									fill: var(--neutrals-900);
								}
							}
						}
					}
				}
				.share-activity-link {
					min-width: var(--px-70);
					width: auto;
					max-width: var(--px-140);
					white-space: nowrap;
					display: inline-flex;
					align-items: center;
					justify-content: flex-end;
					cursor: pointer;
					margin-right: var(--px-20);
					line-height: var(--px-22);
					font-weight: 700;
					font-size: var(--px-14);
					line-height: var(--px-22);
					color: var(--blue-600);
					&:hover {
						.shareActivityText {
							.ellipses {
								div {
									text-decoration: underline;
									cursor: pointer;
								}
							}
						}
					}
					.shareActivityIcon {
						width: var(--px-18);
						height: var(--px-18);
						svg {
							width: var(--px-18) !important;
							height: var(--px-18) !important;
							fill: var(--blue-600);
							path {
								fill: var(--blue-600);
							}
						}
					}
					.shareActivityText {
						font-weight: 700;
						font-size: var(--px-14);
						line-height: var(--px-22);
						color: var(--blue-600);
						width: calc(100% - var(--px-20));
						margin-left: var(--px-5);
						display: inline-block;
						white-space: nowrap;
						text-overflow: ellipsis;
						overflow: hidden;
					}
					&.shareActivityLinkDisabled {
						.shareActivityIcon {
							svg {
								fill: var(--neutrals-700);
								path {
									fill: var(--neutrals-700);
								}
							}
						}
						.shareActivityText {
							color: var(--neutrals-700);
						}
						&:hover {
							.shareActivityText {
								text-decoration: none;
							}
						}
					}
				}
				.FITFormPrintIcon {
					min-width: var(--px-70);
					width: auto;
					max-width: var(--px-100);
					white-space: nowrap;
					display: inline-flex;
					align-items: center;
					margin-right: var(--px-20);
					line-height: var(--px-22);
					cursor: pointer;
					&:hover {
						.PrintIconLabel {
							text-decoration: underline;
							cursor: pointer;
						}
					}
					.motif-icon {
						display: inline-flex;
						line-height: var(--px-22);
						cursor: pointer;
						svg {
							width: var(--px-18) !important;
							height: var(--px-18) !important;
						}
					}
					.PrintIconLabel {
						display: inline-flex;
						font-weight: 700;
						font-size: var(--px-14);
						line-height: var(--px-22);
						margin-left: var(--px-5);
						color: var(--blue-600);
						cursor: pointer;
						width: auto;
						max-width: calc(100% - var(--px-20));
					}
				}
				.StyledRelatedAssignments {
					display: inline-flex;
					margin-right: var(--px-16);
					.assignedtoUserIdIcon {
						.userdata {
							width: auto;
						}
					}
					.assignedto-dropdownportal {
						margin-right: 0;
					}
				}
				.motif-dropdown {
					.motif-button {
						height: var(--px-36) !important;
						width: var(--px-36);
						border: 0;
						.motif-icon svg {
							color: var(--neutrals-900);
						}
					}
				}
				.signoffs-wrapper {
					min-width: var(--px-170);
					margin-right: var(--px-16);
					.StyledSignOff {
						min-width: var(--px-170);
						&.evidenceSignOff {
							padding: 0;
							justify-content: unset;
							margin: 0;
							gap: var(--px-12);
							.StyledSignOffItem {
								margin-left: 0;
								margin-right: 0;
								.signOffContainer {
									width: var(--px-42);
									.signOffButton {
										width: var(--px-42);
										.btnLabel {
											max-width: calc(100% - var(--px-16));
											width: auto;
											line-height: var(--px-18);
											font-weight: 400;
											.signOffLabel {
												width: 100%;
												line-height: var(--px-18);
											}
										}
										.motif-icon svg {
											height: var(--px-16);
											width: var(--px-16);
										}
									}
								}
							}
						}
					}
				}
				.MarkCompleteWrapper {
					min-width: var(--px-28);
					display: inline-flex;
					margin-right: 0;
					width: auto;
					margin-right: var(--px-16);
				}
				.CommentsValidationWrapper {
					display: inline-flex;
					align-items: center;
					width: auto;
					max-width: var(--px-160);
					margin-left: var(--px-16);
					background-color: var(--neutrals-00white);
					border: var(--px-1) solid var(--neutrals-300);
					border-radius: var(--px-20);
					margin-right: var(--px-10);
					height: var(--px-32);
					&.CommentIconNotVisible {
						&.ValidationIconNotVisible {
							border: 0;
							display: none;
						}
					}
					.StyledCommentsMotifDropdownContainer {
						&.StyledCommentsMotifDropdownContainerOpen {
							.StyledSectionCommentCounter {
								background-color: var(--neutrals-900);
								color: var(--neutrals-00white);
								border-radius: var(--px-20) 0 0 var(--px-20);
								svg {
									color: var(--neutrals-00white) !important;
								}
								.CommentsCounter {
									color: var(--neutrals-00white);
								}
							}
						}
						.motif-dropdown {
							.motif-dropdown-menu {
								max-height: var(--px-214) !important;
								.StyledCommentsList {
									max-height: var(--px-154) !important;
								}
							}
						}
					}
					.StyledSectionCommentCounter {
						display: inline-flex;
						justify-content: center;
						align-items: center;
						padding: 0;
						min-height: var(--px-32);
						min-width: var(--px-60);
						width: auto;
						max-width: var(--px-60);
						font-weight: 600;
						cursor: pointer;
						&:hover,
						&:focus {
							background-color: var(--neutrals-900);
							color: var(--neutrals-00white);
							border-radius: var(--px-20) !important;
							cursor: pointer !important;
							svg {
								color: var(--neutrals-00white) !important;
							}
							.CommentsCounter {
								color: var(--neutrals-00white);
							}
						}
						& + .SectionValidationWrapper {
							.motif-dropdown-portal {
								&.StyledMotifDropdownPortal {
									&:hover,
									&:focus,
									&.StyledMotifDropdownPortalOpen {
										border-radius: 0 var(--px-20) var(--px-20) 0;
									}
								}
							}
						}
						svg {
							cursor: pointer;
							color: var(--neutrals-1000) !important;
							width: var(--px-16) !important;
							height: var(--px-16) !important;
							vertical-align: middle;
						}
					}
					.CommentsValidationDropdown {
						.motif-dropdown {
							&.activated,
							&.motif-dropdown-open {
								#cmtIcon {
									svg {
										color: var(--neutrals-00white) !important;
									}
								}
							}
							.motif-dropdown-trigger {
								height: auto;
							}
						}
						&:hover,
						&:focus {
							.motif-dropdown {
								.motif-dropdown-trigger {
									#cmtIcon {
										svg {
											color: var(--neutrals-00white) !important;
										}
									}
									.CommentsCounter {
										color: var(--neutrals-00white) !important;
									}
								}
							}
						}
					}
					.motif-dropdown-portal {
						min-height: var(--px-32);
						display: flex;
						align-items: center;
						justify-content: center;
						&.StyledMotifDropdownPortal {
							&:hover,
							&:focus,
							&.StyledMotifDropdownPortalOpen {
								background-color: var(--neutrals-900);
								color: var(--red-600);
								cursor: pointer;
								svg {
									color: var(--red-600) !important;
								}
								.validationcount {
									color: var(--red-600) !important;
								}
							}
							.motif-dropdown-trigger {
								padding: 0;
								min-width: var(--px-60);
								min-height: auto;
								justify-content: center;
								display: inline-flex;
								.ValidationIcon {
									svg {
										height: var(--px-16);
										width: var(--px-16);
									}
								}
							}
						}
					}
					&.CommentIconVisible {
						&.ValidationIconVisible {
							.CommentsValidationDropdown {
								.motif-dropdown {
									border-radius: var(--px-20) 0 0 var(--px-20) !important;
								}
							}
							.StyledSectionCommentCounter {
								&:hover,
								&:focus {
									border-radius: var(--px-20) 0 0 var(--px-20) !important;
								}
							}
							.StyledCommentsMotifDropdownContainer {
								&:hover {
									.motif-dropdown {
										border-radius: var(--px-20) 0 0 var(--px-20) !important;
									}
								}
								&:hover + .SectionValidationWrapper {
									.motif-dropdown-portal {
										.motif-dropdown-trigger {
											.ValidationIcon {
												border-left: var(--px-1) solid var(--neutrals-00white);
											}
										}
									}
								}
								&.StyledCommentsMotifDropdownContainerOpen {
									.StyledSectionCommentCounter {
										border-radius: var(--px-20) 0 0 var(--px-20) !important;
									}
									& + .SectionValidationWrapper {
										.motif-dropdown-portal {
											.motif-dropdown-trigger {
												.ValidationIcon {
													border-left: var(--px-1) solid var(--neutrals-00white);
												}
											}
										}
									}
								}
							}
							.SectionValidationWrapper {
								.motif-dropdown-portal {
									&:hover,
									&:focus,
									&.StyledMotifDropdownPortalOpen {
										border-radius: 0 var(--px-20) var(--px-20) 0 !important;
										.motif-dropdown-trigger {
											.ValidationIcon {
												border-left: var(--px-1) solid var(--neutrals-900);
											}
										}
									}
									.motif-dropdown-trigger {
										.ValidationIcon {
											border-left: var(--px-1) solid var(--neutrals-300);
										}
									}
								}
							}
						}
					}
					&.ValidationIconVisible {
						.SectionValidationWrapper {
							.motif-dropdown-portal {
								&:hover,
								&:focus,
								&.StyledMotifDropdownPortalOpen {
									border-radius: var(--px-20) !important;
								}
							}
						}
					}
					&.CommentIconVisible {
						.StyledSectionCommentCounter {
							&:hover,
							&:focus {
								border-radius: var(--px-20) !important;
							}
						}
						.StyledCommentsMotifDropdownContainer {
							&.StyledCommentsMotifDropdownContainerOpen {
								.StyledSectionCommentCounter {
									border-radius: var(--px-20) !important;
								}
							}
						}
					}
				}
				.SectionValidationWrapper {
					display: inline-flex;
					justify-content: center;
					align-items: center;
					min-width: var(--px-60);
					/* .motif-dropdown {
						background-color: var(--neutrals-900);
						color: var(--neutrals-00white);
						border-radius: var(--px-15);
						width: auto;
						max-width: var(--px-120);
						padding: 0 var(--px-5);
						align-items: center;
						display: inline-flex;
						justify-content: center;
						.motif-dropdown-trigger {
							display: flex;
							align-items: center;
							svg {
								color: var(--neutrals-00white) !important;
								width: var(--px-16) !important;
								height: var(--px-16) !important;
							}
							.ValidationIcon {
								display: flex;
								align-items: center;
								justify-content: center;
								.ValidationImg {
									color: var(--neutrals-00white) !important;
								}
								.validationcount {
									color: var(--neutrals-00white) !important;
									padding: 0 var(--px-5);
									font-size: var(--px-12);
									font-weight: 600;
									max-width: var(--px-40);
									&.TaskArrow {
										max-width: var(--px-60);
									}
								}
							}
						}
					} */
					.motif-dropdown-menu {
						margin-top: var(--px-12);
						.motif-dropdown-item {
							&:first-child {
								background-color: var(--neutrals-100) !important;
								border-bottom: var(--px-1) solid var(--neutrals-300) !important;
								margin-bottom: var(--px-5);
							}
							.validationDropdownHeading {
								padding: 0 !important;
								width: 100%;
								.motif-icon {
									margin: 0 0 0 auto !important;
									width: var(--px-20) !important;
								}
							}
						}
					}
					.motif-dropdown-menu[x-placement^='bottom-right']:before {
						right: var(--px-24);
						@media only screen and (max-width: 1400px) {
							right: var(--px-22);
						}
					}
					.dropdownValidationCount {
						color: var(--neutrals-900);
					}
					.dropdownValidationName {
						&.cursorPointer {
							color: var(--blue-600);
						}
					}
				}
			}
			.signoffs-wrapper {
				display: flex;
				min-width: var(--px-220);
				max-width: 100%;
				justify-content: flex-start;
				align-items: flex-end;
				padding: 0;
				.MotifDropDownPortalWrapper {
					.motif-dropdown {
						margin-left: 0 !important;
					}
				}
			}
		}
	}
	.SectionTopComponent {
		display: flex;
		align-items: flex-start;
		margin-top: var(--px-30);
		padding: 0 var(--px-80) 0 var(--px-40) !important;
		width: 100%;
		font-size: var(--px-32);
		font-weight: 600;
		line-height: var(--px-32);
	}
	.SectionTopNameWrapper {
		display: flex;
		align-items: flex-start;
		margin: var(--px-40) 0 var(--px-16) 0;
		padding: 0 var(--px-80) 0 var(--px-40) !important;

		&.BodyDetailsSectionTopNameWrapper {
			margin: var(--px-33) 0 var(--px-32) 0;
		}

		.SnapshotDetailWrapper {
			display: flex;
			max-width: calc(100% - var(--px-450));
			.StyledTrackChangesHeaderDisplay {
				width: 100%;
				.track-changes-header-display-Wrapper {
					width: 100%;
				}
			}
		}
		&.nosection {
			display: none;
		}
		.sectionNameTrackChangesWrapper {
			display: flex;
			flex-direction: column;
			width: calc(100% - var(--px-450));
		}
		.SectionNameWrapper {
			font-size: var(--px-32);
			font-weight: 600;
			padding: 0;
			display: inline-flex;
			align-items: center;
			height: var(--px-40);
			.SectionNameDetails {
				width: auto;
				max-width: calc(100% - var(--px-300));
				padding-right: var(--px-10);
				font-size: var(--px-32);
				font-weight: 600;
				line-height: normal;
				display: inline-flex;
				&.BodyDetailsSectionName {
					font-size: var(--px-24);
					font-weight: 400;
					line-height: var(--px-28);
				}
				.StyledFormRelatedEntities {
					height: var(--px-40);
				}
				.shared-icon {
					height: var(--px-40);
				}
				.motif-icon {
					margin-left: var(--px-12);
					svg {
						width: var(--px-24);
						height: var(--px-24);
						path {
							fill: var(--neutrals-900);
							stroke: var(--neutrals-900);
							stroke-width: 0;
						}
					}
				}
			}
			.shared-icon {
				font-size: var(--px-12);
				font-weight: 600;
				line-height: var(--px-16);
				height: var(--px-32);
				color: var(--neutrals-900);
				display: flex;
				align-items: center;
				border: var(--px-1) solid var(--neutrals-300);
				background-color: var(--neutrals-00white);
				border-radius: var(--px-12);
				padding: var(--px-4) var(--px-12);
			}
		}
		.SectionRighSideWrapper {
			margin: 0 0 0 auto;
			width: auto;
			max-width: var(--px-450);
			display: flex;
			align-items: center;
			.custom-icon {
				margin-left: var(--px-16);
				.motif-icon {
					display: flex;
					justify-content: center;
					color: var(--neutrals-700);
					svg {
						width: var(--px-20);
						height: var(--px-20);
					}
				}
			}
			.StyledTrackChangesIcon {
				margin-right: 0;
			}
			.StyledCommentsMotifDropdownContainer {
				.motif-dropdown {
					.motif-dropdown-menu {
						max-height: var(--px-214) !important;
						.StyledCommentsList {
							max-height: var(--px-136) !important;
						}
					}
				}
			}
		}
		.ManageAccountBtn {
			width: auto;
			max-width: var(--px-165);
		}
		.LinkSeparator {
			margin: 0 var(--px-10);
			color: var(--blue-600);
			font-weight: bold;
			line-height: var(--px-22);
		}
		.StyledManageRisk {
			width: auto;
			max-width: var(--px-165);
		}
		.StyledManageAccount {
			width: auto;
			max-width: var(--px-165);
		}
		.StyledManageEstimate {
			width: auto;
			max-width: var(--px-165);
		}
		.CommentsValidationWrapper {
			display: inline-flex;
			align-items: center;
			width: auto;
			max-width: var(--px-160);
			margin-left: var(--px-16);
			background-color: var(--neutrals-00white);
			border: var(--px-1) solid var(--neutrals-300);
			border-radius: var(--px-20);
			height: var(--px-32);
			&.CommentIconNotVisible {
				&.ValidationIconNotVisible {
					border: 0;
					display: none;
				}
			}
			.StyledCommentsMotifDropdownContainer {
				&.StyledCommentsMotifDropdownContainerOpen {
					.StyledSectionCommentCounter {
						background-color: var(--neutrals-900);
						color: var(--neutrals-00white);
						border-radius: var(--px-20) 0 0 var(--px-20);
						svg {
							color: var(--neutrals-00white) !important;
						}
						.CommentsCounter {
							color: var(--neutrals-00white);
						}
					}
				}
			}
			.StyledSectionCommentCounter {
				display: inline-flex;
				justify-content: center;
				align-items: center;
				padding: 0;
				min-height: var(--px-32);
				min-width: var(--px-60);
				width: auto;
				max-width: var(--px-60);
				font-weight: 600;
				cursor: default !important;
				&:hover,
				&:focus {
					background-color: var(--neutrals-900);
					color: var(--neutrals-00white);
					border-radius: var(--px-20) 0 0 var(--px-20) !important;
					cursor: pointer !important;
					svg {
						color: var(--neutrals-00white) !important;
					}
					.CommentsCounter {
						color: var(--neutrals-00white);
					}
				}
				& + .SectionValidationWrapper {
					.motif-dropdown-portal {
						&.StyledMotifDropdownPortal {
							&:hover,
							&:focus,
							&.StyledMotifDropdownPortalOpen {
								border-radius: 0 var(--px-20) var(--px-20) 0;
							}
						}
					}
				}
				svg {
					cursor: pointer !important;
					color: var(--neutrals-700);
					width: var(--px-16) !important;
					height: var(--px-16) !important;
					vertical-align: middle;
				}
			}
			.motif-dropdown-portal {
				min-height: var(--px-32);
				display: flex;
				align-items: center;
				justify-content: center;
				&.StyledMotifDropdownPortal {
					&:hover,
					&:focus,
					&.StyledMotifDropdownPortalOpen {
						background-color: var(--neutrals-900);
						color: var(--red-600);
						cursor: pointer;
						svg {
							color: var(--red-600) !important;
						}
						.validationcount {
							color: var(--red-600) !important;
						}
					}
					.motif-dropdown-trigger {
						min-width: var(--px-60);
						min-height: var(--px-24);
						justify-content: center;
						display: inline-flex;
						align-items: center;
						.ValidationIcon {
							svg {
								height: var(--px-16);
								width: var(--px-16);
							}
						}
					}
				}
			}
			&.CommentIconVisible {
				&.ValidationIconVisible {
					.StyledSectionCommentCounter {
						&:hover,
						&:focus {
							border-radius: var(--px-20) 0 0 var(--px-20) !important;
						}
					}
					.StyledCommentsMotifDropdownContainer {
						&:hover + .SectionValidationWrapper {
							.motif-dropdown-portal {
								.motif-dropdown-trigger {
									.ValidationIcon {
										border-left: var(--px-1) solid var(--neutrals-00white);
									}
								}
							}
						}
						&.StyledCommentsMotifDropdownContainerOpen {
							& + .SectionValidationWrapper {
								.motif-dropdown-portal {
									.motif-dropdown-trigger {
										.ValidationIcon {
											border-left: var(--px-1) solid var(--neutrals-00white);
										}
									}
								}
							}
							.StyledSectionCommentCounter {
								border-radius: var(--px-20) 0 0 var(--px-20) !important;
							}
						}
					}
					.SectionValidationWrapper {
						.motif-dropdown-portal {
							&:hover,
							&:focus,
							&.StyledMotifDropdownPortalOpen {
								border-radius: 0 var(--px-20) var(--px-20) 0 !important;
								.motif-dropdown-trigger {
									.ValidationIcon {
										border-left: var(--px-1) solid var(--neutrals-900);
									}
								}
							}
							.motif-dropdown-trigger {
								.ValidationIcon {
									border-left: var(--px-1) solid var(--neutrals-300);
								}
							}
						}
					}
				}
			}
			&.ValidationIconVisible {
				.SectionValidationWrapper {
					.motif-dropdown-portal {
						&:hover,
						&:focus,
						&.StyledMotifDropdownPortalOpen {
							border-radius: var(--px-20) !important;
						}
					}
				}
			}
			&.CommentIconVisible {
				.StyledSectionCommentCounter {
					&:hover,
					&:focus {
						border-radius: var(--px-20) !important;
					}
				}
				.StyledCommentsMotifDropdownContainer {
					&.StyledCommentsMotifDropdownContainerOpen {
						.StyledSectionCommentCounter {
							border-radius: var(--px-20) !important;
						}
					}
				}
			}
		}
		.SectionValidationWrapper {
			display: inline-flex;
			justify-content: center;
			align-items: center;
			height: var(--px-32);
			/* .motif-dropdown {
				background-color: var(--neutrals-900);
				color: var(--neutrals-00white);
				border-radius: var(--px-15);
				width: auto;
				max-width: var(--px-120);
				padding: 0 var(--px-5);
				align-items: center;
				display: inline-flex;
				justify-content: center;
				.motif-dropdown-trigger {
					display: flex;
					align-items: center;
					svg {
						color: var(--neutrals-00white) !important;
						width: var(--px-16) !important;
						height: var(--px-16) !important;
					}
					.ValidationIcon {
						display: flex;
						align-items: center;
						justify-content: center;
						.ValidationImg {
							color: var(--neutrals-00white) !important;
						}
						.validationcount {
							color: var(--neutrals-00white) !important;
							padding: 0 var(--px-5);
							font-size: var(--px-12);
							font-weight: 600;
							max-width: var(--px-40);
							&.TaskArrow {
								max-width: var(--px-60);
							}
						}
					}
				}
			} */
			.motif-dropdown-menu {
				margin-top: var(--px-12);
				.motif-dropdown-item {
					&:first-child {
						background-color: var(--neutrals-100) !important;
						border-bottom: var(--px-1) solid var(--neutrals-300) !important;
						margin-bottom: var(--px-5);
					}
					.validationDropdownHeading {
						padding: 0 !important;
						width: 100%;
						.motif-icon {
							margin: 0 0 0 auto !important;
							width: var(--px-20) !important;
						}
					}
				}
			}
			.motif-dropdown-menu[x-placement^='bottom-right']:before {
				right: var(--px-24);
				@media only screen and (max-width: 1400px) {
					right: var(--px-22);
				}
			}
			.dropdownValidationCount {
				color: var(--neutrals-900);
			}
			.dropdownValidationName {
				&.cursorPointer {
					color: var(--blue-600);
				}
			}
		}
	}
	.FormDetailSummary {
		padding: var(--px-20) 0 0 var(--px-5);
		display: flex;
		flex-direction: column;
		height: calc(100% - var(--px-45));
	}
	.StyledAresFormBodyContainer {
		height: auto;
		padding: 0 var(--px-80) 0 var(--px-40) !important;
		margin: 0 !important;
	}
	.emptyContainer {
		color: var(--neutrals-700);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: var(--px-14);
		font-weight: 300;
	}
	.task-info {
		margin-top: var(--px-10);
		display: flex;
		width: 100%;
		margin-left: var(--px-15);
		display: flex;
		align-items: flex-start;
		width: calc(100% - var(--px-75));
		min-height: var(--px-60);
		width: calc(100% - var(--px-300));
		padding-right: var(--px-10);
		font-size: var(--px-20);
		width: 100%;
	}
`;
